import getFromCdn from '@anm/helpers/getFromCdn';
import usePageParams from '@anm/hooks/usePageParams';
import { NextInitialProps } from 'anm-next';
import React, { memo, useCallback } from 'react';
import { useSelector } from 'react-redux';

import bootstrapPage, { BootstrapPageProps } from '../HOCs/bootstrapPage';
import injectSagasAndReducers from '../HOCs/injectSagasAndReducers';
import CalendarEvents from '../components/CalendarEvents';
import MetaHead from '../components/MetaHead';
import WaveEventModal from '../components/WaveEventModal';
import Layout from '../components/layouts/Layout';
import TemplateVideoObject from '../components/templates/TemplateVideoObject';
import compose from '../helpers/compose';
import routes from '../routes';
import categoryReducer from '../store/modules/category';
import filterReducer from '../store/modules/filter';
import videoTemplateReducer, { templateSaga } from '../store/modules/template';
import videoReducer, { videoActions, videoSaga } from '../store/modules/video';
import waveEventsReducer, { waveEventsActions, waveEventsSaga, waveEventsSelectors } from '../store/modules/waveEvents';
import { selectWaveEventPreview } from '../store/modules/waveEvents/selectors';

import Page404 from './404';

type WaveEventPageQuery = {
  eventId: string;
  weekDate: string;
};

type HOCs = BootstrapPageProps;

const MemorizedCalendarEvents = memo(CalendarEvents);

const isCalendar404 = (code?: number) => code && [403, 404].includes(code);

const WaveEventPage = ({ path }: HOCs) => {
  const { noforward } = usePageParams();
  const currentWaveEvent = useSelector(waveEventsSelectors.selectCurrentWaveEvent);
  const currentWaveEventError = useSelector(waveEventsSelectors.selectCurrentWaveEventError);

  const handleModalClose = useCallback(() => {
    const { Router } = routes;
    if (!Router.query) return;

    const { weekDate } = Router.query as WaveEventPageQuery;
    noforward
      ? Router.back()
      : Router.pushRoute('calendar', {
          weekDate
        });
  }, [noforward]);

  const preview = selectWaveEventPreview(currentWaveEvent);
  const defaultPreview = {
    url: `${getFromCdn('images/data/social_calendar_preview.jpg')}`,
    size: { width: 400, height: 300 }
  };
  const { url: previewUrl, size: previewSize } = preview || defaultPreview;

  const { title: eventTitle } = currentWaveEvent;
  const title = `${eventTitle} | Free Social Calendar`;
  const description = `Find great social content ideas for ${eventTitle} with this free social calendar. Get free video templates, hashtags, and content tips.`;

  if (isCalendar404(currentWaveEventError?.code)) return <Page404 />;

  return (
    <>
      {currentWaveEvent && <TemplateVideoObject />}
      <MetaHead path={path} size={previewSize} title={title} preview={previewUrl} description={description} />
      <Layout navigationVariant="white">
        <MemorizedCalendarEvents />
      </Layout>

      <WaveEventModal onModalClose={handleModalClose} />
    </>
  );
};

WaveEventPage.getInitialProps = ({ query, store, res }: NextInitialProps<WaveEventPageQuery>) => {
  const { eventId, weekDate } = query;

  store.dispatch(waveEventsActions.fetchWaveEventList(weekDate));
  store.dispatch(waveEventsActions.setCurrentTemplateFromWaveEvent(weekDate, eventId));
  store.dispatch(videoActions.getVideoFormats());

  const onAsyncDone = () => {
    const currentWaveEventError = waveEventsSelectors.selectCurrentWaveEventError(store.getState());

    if (res && isCalendar404(currentWaveEventError?.code)) {
      res.statusCode = 404;
    }
  };

  return { onAsyncDone };
};

export default compose(
  bootstrapPage,
  injectSagasAndReducers(
    () => [...videoSaga(), ...templateSaga(), ...waveEventsSaga()],
    {
      video: videoReducer,
      filter: filterReducer,
      category: categoryReducer,
      template: videoTemplateReducer,
      waveEvents: waveEventsReducer
    },
    { id: 'WaveEventPage' }
  )
)(WaveEventPage);
